import { useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { useApp } from './AppContext';

const VERIFICATION_FLAG_KEY = 'civicbeat_email_verified_shown';

export function EmailVerificationTracker() {
  const { showToast } = useApp();
  const hasCheckedInitial = useRef(false);
  const toastShown = useRef(false);

  useEffect(() => {
    const showVerificationToast = () => {
      if (toastShown.current) return;
      toastShown.current = true;

      sessionStorage.setItem(VERIFICATION_FLAG_KEY, 'true');
      showToast('Email verified. Welcome to civicbeat.');

      // Clean up URL hash ONLY if not a recovery link
      setTimeout(() => {
        const hash = window.location.hash;
        if (hash && hash.includes('access_token')) {
          const params = new URLSearchParams(hash.substring(1));
          const type = params.get('type');

          // Don't clear hash for password recovery - it needs to stay for the reset page
          if (type !== 'recovery') {
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
          }
        }
      }, 100);
    };

    // Check if we've already shown the toast in this session
    if (sessionStorage.getItem(VERIFICATION_FLAG_KEY)) {
      return;
    }

    const checkInitialVerification = async () => {
      if (hasCheckedInitial.current) return;
      hasCheckedInitial.current = true;

      // Check URL hash for verification token
      const hash = window.location.hash;
      console.log('[EmailVerificationTracker] Checking hash:', hash);

      // Supabase uses type=signup for email confirmation
      // Also check for access_token which indicates successful auth
      if (hash && hash.includes('access_token')) {
        const params = new URLSearchParams(hash.substring(1));
        const type = params.get('type');

        console.log('[EmailVerificationTracker] Hash type:', type);

        // IMPORTANT: Skip password recovery - it's handled by ResetPassword component
        if (type === 'recovery') {
          console.log('[EmailVerificationTracker] Skipping recovery type - handled by ResetPassword');
          return;
        }

        // Check if this is email verification (type=signup or type=magiclink)
        if (type === 'signup' || type === 'magiclink' || type === 'email') {
          // Get current session to confirm user is authenticated
          const { data: { session } } = await supabase.auth.getSession();

          if (session?.user) {
            console.log('[EmailVerificationTracker] Email verified for user:', session.user.email);
            showVerificationToast();
          }
        }
      }
    };

    checkInitialVerification();

    // Also listen for auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[EmailVerificationTracker] Auth event:', event);

      // Check if already shown
      if (sessionStorage.getItem(VERIFICATION_FLAG_KEY) || toastShown.current) {
        return;
      }

      // Handle sign in from email verification
      if (event === 'SIGNED_IN' && session?.user) {
        const hash = window.location.hash;

        // Check if this is from email verification
        if (hash && hash.includes('access_token')) {
          const params = new URLSearchParams(hash.substring(1));
          const type = params.get('type');

          // Skip recovery - handled by ResetPassword component
          if (type === 'recovery') {
            return;
          }

          if (type === 'signup' || type === 'magiclink' || type === 'email') {
            console.log('[EmailVerificationTracker] Email verified via auth event for:', session.user.email);
            showVerificationToast();
          }
        }
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [showToast]);

  return null;
}
